import { AlertCircle, ChevronLeft, FileText, Mail, MapPin, Phone, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/applications');

interface ApplicationDetailData {
  id: string;
  status: string;
  receiptNumber: string;
  applicant: {
    nameLast: string;
    nameFirst: string;
    nameKanaLast: string;
    nameKanaFirst: string;
    email: string;
  };
  offering: {
    offering: { name: string };
    structure: { nameFormal: string };
  };
  venue?: { name: string };
  data: Array<{
    id: string;
    fieldKey: string;
    fieldValue: string;
  }>;
  payments: Array<{
    id: string;
    method: string;
    createdAt: string;
    amount: number;
  }>;
}

export default function ApplicationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState<ApplicationDetailData | null>(null);

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/${id}`);
        if (res.ok) {
          const data = (await res.json()) as ApplicationDetailData;
          setApplication(data);
        }
      } catch (error) {
        console.error('Failed to fetch application', error);
      } finally {
        setLoading(false);
      }
    };
    fetchApplication();
  }, [id]);

  if (loading) return <div className="p-8 text-center text-gray-500">読み込み中...</div>;
  if (!application) return <div className="p-8 text-center text-red-500">データが見つかりません</div>;

  const applicant = application.applicant;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/applications/search')}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                {applicant.nameLast} {applicant.nameFirst}
              </h1>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {application.status}
              </Badge>
            </div>
            <p className="text-gray-500">受付番号: {application.receiptNumber || '未発行'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">受験票再生成</Button>
          <Button className="bg-blue-600 hover:bg-blue-700">修正・保存</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Fixed Bio */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" />
                志願者プロファイル
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 py-2">
                <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                  <User className="w-8 h-8" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {applicant.nameKanaLast} {applicant.nameKanaFirst}
                  </div>
                  <div className="text-xs text-gray-500">2006年5月12日生 (18歳)</div>
                </div>
              </div>
              <Separator />
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span>{applicant.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>090-1234-5678</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span>
                    〒100-0001
                    <br />
                    東京都千代田区千代田1-1
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-50 border-dashed">
            <CardHeader>
              <CardTitle className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                管理用メモ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                className="w-full h-32 text-sm bg-transparent border-none focus:ring-0 resize-none"
                placeholder="事務局向けのメモを入力してください..."
              ></textarea>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Dynamic Data Tags */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="application" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-12 p-0 gap-8">
              <TabsTrigger
                value="application"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none px-2 h-12 font-bold"
              >
                出願内容
              </TabsTrigger>
              <TabsTrigger
                value="payment"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none px-2 h-12 font-bold"
              >
                決済・請求
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none px-2 h-12 font-bold"
              >
                ログ履歴
              </TabsTrigger>
            </TabsList>

            <TabsContent value="application" className="pt-6 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    募集枠情報
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 font-bold uppercase">入試区分</div>
                    <div className="text-sm font-medium">{application.offering?.offering?.name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 font-bold uppercase">募集学部・学科</div>
                    <div className="text-sm font-medium">{application.offering?.structure?.nameFormal}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 font-bold uppercase">試験日</div>
                    <div className="text-sm font-medium">2025年2月15日 (土)</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 font-bold uppercase">試験会場</div>
                    <div className="text-sm font-medium">{application.venue?.name || '本学会場'}</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">志願項目入力内容</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {application.data?.map((item) => (
                    <div key={item.id} className="grid grid-cols-3 gap-2 py-2 border-b last:border-0">
                      <div className="text-sm text-gray-600 col-span-1">{item.fieldKey}</div>
                      <div className="text-sm font-medium text-gray-900 col-span-2">{item.fieldValue}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="payment" className="pt-6">
              <Card className="border-l-4 border-l-orange-500">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <div className="text-sm text-gray-500 font-bold uppercase">現在の決済状況</div>
                      <div className="text-2xl font-black text-gray-900">入金済 (PAID)</div>
                    </div>
                    <Button variant="outline" size="sm">
                      決済取消
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {application.payments?.map((payment) => (
                      <div key={payment.id} className="flex justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="text-sm font-bold font-mono">#{payment.id.slice(0, 8)}</div>
                          <div className="text-xs text-gray-500">
                            {payment.method} / {new Date(payment.createdAt).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold">¥{payment.amount.toLocaleString()}</div>
                          <Badge className="bg-emerald-500">SUCCESS</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
